'use client'

import {Accordion as AccordionContainer, AccordionItem, AccordionItemButton, AccordionItemHeading, AccordionItemPanel, AccordionItemState} from '@gnu/react-accessible-accordion'
import {Button, PortableTextProps, SanityBlockContent} from '@local/ui/components'
import {cx} from '@local/utils/cx'

import {useId} from 'react'

export interface AccordionProps {
	className?: string
	textClassName?: string
	items: Array<{
		title: string
		text: PortableTextProps[] | string
	}>
	preExpanded?: string[]
}

export const Accordion = ({className, textClassName, items, preExpanded = ['accordion-item-0'], ...rest}: AccordionProps) => {
	const id = useId()
	return !!items?.length && (
		<AccordionContainer
			allowZeroExpanded
			className={cx('w-full [&_.accordion__panel-wrapper]:block', className)}
			preExpanded={preExpanded}
			{...rest}>
			{items.map((item, index) => (
				<AccordionItem
					key={`accordion-item-${index}-${id}`}
					uuid={`accordion-item-${index}-${id}`}
					className='border-t border-grey-1/30'>
					<AccordionItemHeading
						aria-level={2}
						id={`accordion-item-${index}-${id}-heading`}>
						<AccordionItemButton
							className='flex flex-row items-center justify-between py-10'>
							<span className='text-11 uppercase tracking-wider'>{item.title}</span>
							<AccordionItemState>
								{({expanded}) => (
									<Button as='div' label={expanded ? 'Collapse' : 'Expand'}>
										<svg
											width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg' className={cx(
												'w-12 h-12',
											)}>
											<path
												fillRule='evenodd'
												clipRule='evenodd'
												d='M13 7.3592H1V6.63257H13V7.3592Z'
												fill='currentColor' />
											<path
												fillRule='evenodd'
												clipRule='evenodd'
												d='M6.62891 1L6.62891 6.7H7.35891L7.35891 1L6.62891 1ZM6.62891 8.61328V13.0018H7.35891L7.35891 8.61328H6.62891Z'
												fill='currentColor'
												className={cx('transition-transform duration-300 transform-gpu origin-center ease-quart-inout', expanded ? 'rotate-90' : '0')} />
										</svg>
									</Button>
								)}
							</AccordionItemState>
						</AccordionItemButton>
					</AccordionItemHeading>
					<AccordionItemPanel animated>
						<AccordionItemState>
							{({expanded}) => (
								Array.isArray(item.text)
									? (
										<SanityBlockContent
											blocks={item.text}
											styles={{
												normal: 'leading-normal tracking-snug pt-10',
												listStyle: 'text-grey-footer mt-16 mb-10',
												listBulletStyle: 'list-disc [&_li]:mb-3 pl-14 ml-4',
												listNumberStyle: 'list-decimal [&_li]:mb-3 pl-14',
												listItemStyle: 'mb-30 last:mb-0',
											}}
											className={cx(
												'text-12 text-grey-footer text-[350] pb-10 lg:pb-20 [&>*]:first:mt-0 [&>*]:first:pt-0 [&>*]:last:mb-0 [&>*]:last:pb-0',
												expanded ? 'visible' : 'hidden',
												textClassName,
											)} />
									)
									: (
										<p className={cx(
											'text-12 pb-10 lg:pb-20 text-grey-2 text-[350]',
											expanded ? 'visible' : 'hidden',
											textClassName,
										)}>
											{item.text}
										</p>
									)
							)}
						</AccordionItemState>
					</AccordionItemPanel>
				</AccordionItem>
			))}
		</AccordionContainer>
	)
}
